import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAgentAuth } from '../contexts/AgentAuthContext';
import axios from 'axios';
import { Calendar, Clock, MapPin, Users, ArrowLeft, RefreshCw } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

const AgentSlotsViewer = () => {
  const { agent } = useAgentAuth();
  const navigate = useNavigate();
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [locationId, setLocationId] = useState(agent?.locationId || '');
  const [locations, setLocations] = useState([]);
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchLocations();
  }, []);

  useEffect(() => {
    if (locationId && selectedDate) {
      fetchSlots();
    }
  }, [locationId, selectedDate]);

  const fetchLocations = async () => {
    try {
      const response = await axios.get(`${API}/api/locations`);
      setLocations(response.data);
    } catch (error) {
      console.error('Failed to fetch locations:', error);
    }
  };

  const fetchSlots = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/api/slots/available`, {
        params: {
          locationId,
          startDate: selectedDate
        }
      });
      setSlots(response.data.slots || []);
    } catch (error) {
      console.error('Failed to fetch slots:', error);
      setSlots([]);
    } finally {
      setLoading(false);
    }
  };

  const selectedLocation = locations.find(loc => loc._id === locationId);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900">
      {/* Header */}
      <div className="bg-gray-800/50 border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/agent/dashboard')}
              className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
            >
              <ArrowLeft className="text-white" size={24} />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-white">Available Slots</h1>
              <p className="text-gray-400 text-sm">View slot availability by date and location</p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filters */}
        <div className="bg-gray-800 border border-gray-700 rounded-xl p-6 mb-6">
          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <label className="text-white font-medium mb-2 block">Location</label>
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                <select
                  value={locationId}
                  onChange={(e) => setLocationId(e.target.value)}
                  disabled={agent?.locationId}
                  className="w-full pl-11 pr-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-400 disabled:opacity-50"
                >
                  <option value="">Select location</option>
                  {locations.map(loc => (
                    <option key={loc._id} value={loc._id}>{loc.name}</option>
                  ))}
                </select>
              </div>
              {agent?.locationId && (
                <p className="text-yellow-400 text-xs mt-2">Your assigned location is pre-selected</p>
              )}
            </div>

            <div>
              <label className="text-white font-medium mb-2 block">Date</label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full pl-11 pr-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-400"
                />
              </div>
            </div>

            <div className="flex items-end">
              <button
                onClick={fetchSlots}
                disabled={loading || !locationId}
                className="w-full flex items-center justify-center space-x-2 px-6 py-3 bg-yellow-400 hover:bg-yellow-500 text-black font-bold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <RefreshCw size={20} className={loading ? 'animate-spin' : ''} />
                <span>{loading ? 'Loading...' : 'Refresh'}</span>
              </button>
            </div>
          </div>
        </div>

        {/* Location Info */}
        {selectedLocation && (
          <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4 mb-6">
            <h3 className="text-white font-bold mb-2">{selectedLocation.name}</h3>
            <p className="text-gray-400 text-sm mb-2">{selectedLocation.address}</p>
            <p className="text-yellow-400 font-bold">₹{selectedLocation.pricePerJumper?.toLocaleString()} per jumper</p>
          </div>
        )}

        {/* Slots Grid */}
        {loading ? (
          <div className="text-center py-12">
            <RefreshCw className="mx-auto mb-4 text-yellow-400 animate-spin" size={48} />
            <p className="text-gray-400">Loading slots...</p>
          </div>
        ) : slots.length === 0 ? (
          <div className="text-center py-12 bg-gray-800 border border-gray-700 rounded-xl">
            <Clock className="mx-auto mb-4 text-gray-600" size={48} />
            <p className="text-gray-400">No slots available for selected date</p>
            <p className="text-gray-500 text-sm mt-2">Try selecting a different date or location</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {slots.map((slot) => (
              <div
                key={slot._id}
                className="bg-gray-800 border border-gray-700 rounded-xl p-6 hover:border-yellow-400 transition-colors"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-2">
                    <Clock className="text-yellow-400" size={20} />
                    <span className="text-white font-bold">
                      {slot.startTime} - {slot.endTime}
                    </span>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                    slot.availableSeats > 5 ? 'bg-green-500/20 text-green-400' :
                    slot.availableSeats > 0 ? 'bg-yellow-500/20 text-yellow-400' :
                    'bg-red-500/20 text-red-400'
                  }`}>
                    {slot.availableSeats > 0 ? 'Available' : 'Full'}
                  </span>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-400">Total Seats</span>
                    <span className="text-white font-bold">{slot.totalSeats}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-400">Available</span>
                    <span className="text-green-400 font-bold">{slot.availableSeats}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-400">Booked</span>
                    <span className="text-blue-400 font-bold">{slot.bookedSeats}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-400">Duration</span>
                    <span className="text-white">{slot.duration} min</span>
                  </div>
                </div>

                {slot.availableSeats > 0 && (
                  <button
                    onClick={() => navigate('/agent/booking/create', { state: { slotId: slot._id, date: selectedDate } })}
                    className="w-full mt-4 px-4 py-2 bg-yellow-400 hover:bg-yellow-500 text-black font-bold rounded-lg transition-colors"
                  >
                    Book This Slot
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AgentSlotsViewer;